import React from 'react'
import { useStore } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import type { TFunction } from 'i18next'
import type { DocumentCollectionIcon } from '@/graphql/schema'
import type { DocumentCollectionIconOption } from '@/entities/document-collection/document-collection-icons'
import {
  documentCollectionIconOptions,
  getDocumentCollectionIconLabel,
} from '@/entities/document-collection/document-collection-icons'
import { useFieldContext } from '@/shared/form/demo-form-context'
import { CollectionDescriptionMarkdownEditor } from '@/features/document-collection/collection-description-markdown'
import { cn } from '@/shared/lib/utils'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

export function DocumentCollectionNameField() {
  const field = useFieldContext<string>()
  const { t } = useTranslation('documentCollections')
  const inputId = 'document-collection-sheet-name'
  const errorId = 'document-collection-sheet-name-error'
  const errors = useStore(field.store, (state) => state.meta.errors)
  const isTouched = useStore(field.store, (state) => state.meta.isTouched)
  const shouldShowError = isTouched && errors.length > 0

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {t('documentCollections:collectionNameLabel')}
      </Label>
      <Input
        id={inputId}
        value={field.state.value}
        placeholder={t('documentCollections:collectionNamePlaceholder')}
        aria-invalid={shouldShowError}
        aria-describedby={shouldShowError ? errorId : undefined}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
      />
      <p className="text-sm text-muted-foreground">
        {t('documentCollections:collectionNameHint')}
      </p>
      {shouldShowError ? (
        <p id={errorId} className="text-sm text-destructive">
          {t(
            typeof errors[0] === 'string'
              ? errors[0]
              : String(errors[0]?.message ?? ''),
          )}
        </p>
      ) : null}
    </div>
  )
}

export function DocumentCollectionDescriptionField() {
  const field = useFieldContext<string>()
  const { t } = useTranslation('documentCollections')
  const inputId = 'document-collection-sheet-description'

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {t('documentCollections:collectionDescriptionLabel')}
      </Label>
      <CollectionDescriptionMarkdownEditor
        id={inputId}
        value={field.state.value}
        placeholder={t('pages:documentCollectionDescription')}
        onBlur={field.handleBlur}
        onChange={field.handleChange}
      />
      <p className="text-sm text-muted-foreground">
        {t('documentCollections:collectionDescriptionHint')}
      </p>
    </div>
  )
}

export function DocumentCollectionIconField({
  columnCount = 12,
  value,
  onChange,
}: {
  columnCount?: 5 | 12
  value: DocumentCollectionIcon
  onChange: (value: DocumentCollectionIcon) => void
}) {
  const { t } = useTranslation('documentCollections')
  const [search, setSearch] = React.useState('')
  const selectedOption = getDocumentCollectionIconLabel(value, t)
  const Icon =
    documentCollectionIconOptions.find((o) => o.value === value)?.Icon ??
    documentCollectionIconOptions[1].Icon
  const filteredOptions = filterDocumentCollectionIconOptions(search, t)

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-3">
        <div>
          <Label>{t('documentCollections:iconLabel')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('documentCollections:iconHint')}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1.5 text-sm text-foreground">
          <Icon className="size-4 text-muted-foreground" />
          <span>{selectedOption}</span>
        </div>
      </div>
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-muted-foreground" />
        <Input
          value={search}
          className="pl-9"
          placeholder={t('documentCollections:searchIcons')}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <DocumentCollectionIconGrid
        columnCount={columnCount}
        onChange={onChange}
        options={filteredOptions}
        selectedValue={value}
        t={t}
      />
    </div>
  )
}

function DocumentCollectionIconGrid({
  columnCount,
  onChange,
  options,
  selectedValue,
  t,
}: {
  columnCount: 5 | 12
  onChange: (value: DocumentCollectionIcon) => void
  options: Array<DocumentCollectionIconOption>
  selectedValue: DocumentCollectionIcon
  t: TFunction<'documentCollections'>
}) {
  return (
    <div
      className={cn(
        'grid gap-2 rounded-2xl border border-border bg-muted/20 p-3',
        columnCount === 5 ? 'grid-cols-5' : 'grid-cols-12',
      )}
    >
      {options.map((option) => {
        const IconComponent = option.Icon
        const isSelected = option.value === selectedValue
        const iconLabel = getDocumentCollectionIconLabel(option.value, t)

        return (
          <button
            key={option.value}
            type="button"
            aria-label={t('documentCollections:chooseIconAria', {
              icon: iconLabel,
            })}
            aria-pressed={isSelected}
            className={cn(
              'flex aspect-square items-center justify-center rounded-xl border transition-all outline-none',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-foreground',
            )}
            onClick={() => onChange(option.value)}
          >
            <IconComponent className="size-4" />
          </button>
        )
      })}
      {options.length === 0 ? (
        <p
          className={cn(
            'py-6 text-center text-sm text-muted-foreground',
            columnCount === 5 ? 'col-span-5' : 'col-span-12',
          )}
        >
          {t('documentCollections:noIconsFound')}
        </p>
      ) : null}
    </div>
  )
}

function filterDocumentCollectionIconOptions(
  search: string,
  t: TFunction<'documentCollections'>,
) {
  const searchTerm = normalizeIconSearchTerm(search)
  if (!searchTerm) return documentCollectionIconOptions

  return documentCollectionIconOptions.filter((option) =>
    [
      getDocumentCollectionIconLabel(option.value, t),
      option.label,
      option.value,
    ].some((candidate) =>
      normalizeIconSearchTerm(candidate).includes(searchTerm),
    ),
  )
}

function normalizeIconSearchTerm(value: string) {
  return value.trim().replaceAll('_', ' ').toLocaleLowerCase()
}
